/**
 * Canonical IPC authority catalogue. Every channel is intentionally classified
 * here; the Record constraints make new channel declarations fail typecheck
 * until their authority and Cloud-dispatch posture are reviewed.
 */
export const INVOKE_PROJECT_METADATA = {
    'game:create': { scope: 'creation-session', cloud: 'not-applicable' },
    // crucible#3041 split create — the deferred phase-2 trigger; same
    // creation-session scope as game:create (it IS the same create, resumed).
    'game:create:finish': { scope: 'creation-session', cloud: 'not-applicable' },
    'asset-review:active': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:snapshot': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:approve': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:regenerate': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:swap-take': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:command': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'asset-review:finish': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'art:open': { scope: 'workspace', cloud: 'fail-closed' },
    'art:close': { scope: 'workspace', cloud: 'fail-closed' },
    'art:library': { scope: 'workspace', cloud: 'fail-closed' },
    'art:redo': { scope: 'workspace', cloud: 'fail-closed' },
    'art:export': { scope: 'workspace', cloud: 'fail-closed' },
    'art:draft-spec': { scope: 'workspace', cloud: 'fail-closed' },
    'art:add-asset': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:list': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:add': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:detail': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:versions': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:version': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:run': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:validation': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:preview': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:generate': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:revise': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:approve': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:reject': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:unapprove': { scope: 'workspace', cloud: 'fail-closed' },
    'art:model:retry': { scope: 'workspace', cloud: 'fail-closed' },
    // crucible#3007 — `art:*` twins of the `asset-review:*` review/steering
    // channels above; same scope/cloud posture as every other `art:*` entry.
    'art:active': { scope: 'workspace', cloud: 'fail-closed' },
    'build-asset-review:status': { scope: 'workspace', cloud: 'fail-closed' },
    'build-asset-review:retry': { scope: 'workspace', cloud: 'fail-closed' },
    'art:snapshot': { scope: 'workspace', cloud: 'fail-closed' },
    'art:approve': { scope: 'workspace', cloud: 'fail-closed' },
    'art:regenerate': { scope: 'workspace', cloud: 'fail-closed' },
    'art:swap-take': { scope: 'workspace', cloud: 'fail-closed' },
    'art:command': { scope: 'workspace', cloud: 'fail-closed' },
    'art:finish': { scope: 'workspace', cloud: 'fail-closed' },
    'build-asset-review:start-for-test': {
        scope: 'workspace-test-or-dashboard-project',
        cloud: 'fail-closed',
    },
    'build-asset-review:snapshot-for-test': {
        scope: 'workspace-test-or-dashboard-project',
        cloud: 'fail-closed',
    },
    // crucible#3785 — same test-only bypass as its start/snapshot siblings.
    'build-asset-review:approve-for-test': {
        scope: 'workspace-test-or-dashboard-project',
        cloud: 'fail-closed',
    },
    'build-asset-review:release-turn-for-test': {
        scope: 'workspace-test-or-dashboard-project',
        cloud: 'fail-closed',
    },
    'intake:snapshot': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'intake:submit': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'intake:turn': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'intake:begin': { scope: 'creation-session', cloud: 'handler-routed' },
    'intake:recover': { scope: 'workspace', cloud: 'handler-routed' },
    'intake:plan-review-status': {
        scope: 'workspace-or-creation-session',
        cloud: 'handler-routed',
    },
    'intake:retry-plan-review': {
        scope: 'workspace-or-creation-session',
        cloud: 'handler-routed',
    },
    'intake:answer': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'intake:skip': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'intake:command': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'intake:regenerate-moodboard': { scope: 'workspace-or-creation-session', cloud: 'fail-closed' },
    'intake:visual:current': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'intake:visual:retry': { scope: 'workspace-or-creation-session', cloud: 'handler-routed' },
    'game:import:pick-directory': { scope: 'global', cloud: 'not-applicable' },
    'game:import:commit': { scope: 'global', cloud: 'not-applicable' },
    'game:list': { scope: 'global', cloud: 'not-applicable' },
    'project-runtime:get': { scope: 'workspace', cloud: 'handler-routed' },
    'project-runtime:bind': { scope: 'bind', cloud: 'not-applicable' },
    'game:list:lite': { scope: 'global', cloud: 'not-applicable' },
    'game:list:local-lite': { scope: 'global', cloud: 'not-applicable' },
    'game:list:cloud-lite': { scope: 'global', cloud: 'not-applicable' },
    'game:status': { scope: 'workspace-or-dashboard-project', cloud: 'handler-routed' },
    'game-state:get': { scope: 'workspace', cloud: 'handler-routed' },
    'game-state:subscribe': { scope: 'workspace', cloud: 'handler-routed' },
    'game-state:unsubscribe': { scope: 'workspace', cloud: 'handler-routed' },
    'game:status:lite': { scope: 'workspace-or-dashboard-project', cloud: 'handler-routed' },
    'game:open-in-editor': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'install-state:get': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'install-state:retry': { scope: 'workspace', cloud: 'fail-closed' },
    'design-agent-status:get': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'create:deferred-design-status:get': {
        scope: 'workspace-or-dashboard-project',
        cloud: 'fail-closed',
    },
    'agent:get-status': { scope: 'workspace', cloud: 'handler-routed' },
    'agent:history': { scope: 'workspace', cloud: 'handler-routed' },
    'agent:send': { scope: 'workspace', cloud: 'handler-routed' },
    'agent:answer-user-question': { scope: 'workspace', cloud: 'handler-routed' },
    'deps:respond': { scope: 'workspace', cloud: 'handler-routed' },
    // crucible#3491 — same workspace-scoped, handler-routed posture as
    // `deps:respond` (an identical gated-approval lifecycle, this time
    // resolving the template-file-override gate instead of the
    // dependency-request gate).
    'template-file-override:respond': { scope: 'workspace', cloud: 'handler-routed' },
    'agent:interrupt': { scope: 'workspace', cloud: 'handler-routed' },
    'build-game:get-interview-state': { scope: 'workspace', cloud: 'handler-routed' },
    'build-game:set-answer': { scope: 'workspace', cloud: 'fail-closed' },
    'preview:change-request': { scope: 'workspace', cloud: 'fail-closed' },
    'build-game:start-over': { scope: 'workspace', cloud: 'fail-closed' },
    'build-game:skip-interview': { scope: 'workspace', cloud: 'fail-closed' },
    'interview:start-building': { scope: 'workspace', cloud: 'handler-routed' },
    'interview:back-to-plan': { scope: 'workspace', cloud: 'fail-closed' },
    'build:autoPipeline:cancel': { scope: 'workspace', cloud: 'fail-closed' },
    'build:autoPipeline:retry': { scope: 'workspace', cloud: 'fail-closed' },
    'interview:save-plan': { scope: 'workspace', cloud: 'fail-closed' },
    'interview:rebuild-from-plan': { scope: 'workspace', cloud: 'fail-closed' },
    'interview:get-pending-interactions': { scope: 'workspace', cloud: 'handler-routed' },
    'interview:recover': { scope: 'workspace', cloud: 'handler-routed' },
    'build-game:should-renderer-heartbeat': { scope: 'workspace', cloud: 'fail-closed' },
    'app:get-feature-flags': { scope: 'global', cloud: 'not-applicable' },
    'settings:get-resolved': { scope: 'global', cloud: 'not-applicable' },
    'app:get-furnace-status': { scope: 'global', cloud: 'not-applicable' },
    'cloud-account:get-status': { scope: 'global', cloud: 'not-applicable' },
    'cloud-account:sign-in': { scope: 'global', cloud: 'not-applicable' },
    'build-game:interview-status': {
        scope: 'workspace-or-dashboard-project',
        cloud: 'handler-routed',
    },
    'dev:start': { scope: 'workspace', cloud: 'handler-routed' },
    'dev:stop': { scope: 'workspace', cloud: 'handler-routed' },
    'preview:restart': { scope: 'workspace', cloud: 'handler-routed' },
    'preview:add-phone': { scope: 'workspace', cloud: 'fail-closed' },
    'preview:remove-phone': { scope: 'workspace', cloud: 'fail-closed' },
    'preview:capture-area': { scope: 'workspace', cloud: 'fail-closed' },
    'preview:inject-inspector': { scope: 'workspace', cloud: 'fail-closed' },
    'publish:run': { scope: 'workspace', cloud: 'handler-routed' },
    'dashboard:preferences:get': { scope: 'global', cloud: 'not-applicable' },
    'game:discover': { scope: 'global', cloud: 'not-applicable' },
    'game:clone': { scope: 'dashboard-project', cloud: 'not-applicable' },
    'game:archive:set': { scope: 'dashboard-project-or-community', cloud: 'not-applicable' },
    'game:favorite:set': { scope: 'dashboard-project-or-community', cloud: 'not-applicable' },
    'game:delete': { scope: 'dashboard-project', cloud: 'not-applicable' },
    'game:set-display-name': { scope: 'workspace', cloud: 'fail-closed' },
    'game:suggest-name': { scope: 'global', cloud: 'not-applicable' },
    'game:generate-hero': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'concept:generate-pitch': { scope: 'global', cloud: 'not-applicable' },
    'concept:generate-icon': { scope: 'global', cloud: 'not-applicable' },
    'concept:suggest-selections': { scope: 'global', cloud: 'not-applicable' },
    'game:get-tile-image': { scope: 'workspace', cloud: 'fail-closed' },
    'game:set-tile-image': { scope: 'workspace', cloud: 'fail-closed' },
    // File browsing remains a Local-only capability. Consumer Cloud workspaces
    // intentionally do not expose their source tree through the external API.
    'game:files': { scope: 'workspace', cloud: 'fail-closed' },
    'config:get': { scope: 'global', cloud: 'not-applicable' },
    'config:set': { scope: 'global', cloud: 'not-applicable' },
    'tokens:get': { scope: 'global', cloud: 'not-applicable' },
    'tokens:set': { scope: 'global', cloud: 'not-applicable' },
    'tokens:verify': { scope: 'global', cloud: 'not-applicable' },
    'github:get-user': { scope: 'global', cloud: 'not-applicable' },
    'github:repo-access': { scope: 'global', cloud: 'not-applicable' },
    'tokens:test-registry': { scope: 'global', cloud: 'not-applicable' },
    'tokens:clear': { scope: 'global', cloud: 'not-applicable' },
    'app:is-maintainer': { scope: 'global', cloud: 'not-applicable' },
    'app:get-vm-diagnostics': { scope: 'global', cloud: 'not-applicable' },
    'app:check-updates': { scope: 'global', cloud: 'not-applicable' },
    'app:install-update': { scope: 'global', cloud: 'not-applicable' },
    'app:get-version': { scope: 'global', cloud: 'not-applicable' },
    'app:get-pending-update': { scope: 'global', cloud: 'not-applicable' },
    'app:get-whats-new': { scope: 'global', cloud: 'not-applicable' },
    'app:dismiss-whats-new': { scope: 'global', cloud: 'not-applicable' },
    'app:onboarding-complete': { scope: 'global', cloud: 'not-applicable' },
    'app:set-onboarding-complete': { scope: 'global', cloud: 'not-applicable' },
    'app:fal-key-declined': { scope: 'global', cloud: 'not-applicable' },
    'app:set-fal-key-declined': { scope: 'global', cloud: 'not-applicable' },
    'app:get-analytics-opt-out': { scope: 'global', cloud: 'not-applicable' },
    'app:set-analytics-opt-out': { scope: 'global', cloud: 'not-applicable' },
    'app:open-logs': { scope: 'global', cloud: 'not-applicable' },
    'app:get-diagnostics': { scope: 'global', cloud: 'not-applicable' },
    'diagnostics:get-project': { scope: 'settings-local-project', cloud: 'fail-closed' },
    'diagnostics:probe-system-tools': { scope: 'global', cloud: 'not-applicable' },
    'diagnostics:capture-renderer-issue': { scope: 'global', cloud: 'not-applicable' },
    'app:open-bug-report': { scope: 'global', cloud: 'not-applicable' },
    'bug:file': { scope: 'workspace', cloud: 'fail-closed' },
    'bug:cancel': { scope: 'workspace', cloud: 'fail-closed' },
    'bug:get-draft': { scope: 'workspace', cloud: 'fail-closed' },
    'conflicts:get-pending': { scope: 'workspace', cloud: 'fail-closed' },
    'conflicts:resolve-with-ai': { scope: 'workspace', cloud: 'fail-closed' },
    'conflicts:dismiss': { scope: 'workspace', cloud: 'fail-closed' },
    'deploy:get-failure-context': { scope: 'workspace', cloud: 'fail-closed' },
    'deploy:fix-in-agent': { scope: 'workspace', cloud: 'fail-closed' },
    'deploy:watch:start': { scope: 'workspace', cloud: 'fail-closed' },
    'deploy:watch:stop': { scope: 'workspace-teardown', cloud: 'fail-closed' },
    'deploy:check-up-to-date': { scope: 'workspace', cloud: 'fail-closed' },
    'chat:load': { scope: 'workspace', cloud: 'handler-routed' },
    'chat:save': { scope: 'workspace', cloud: 'handler-routed' },
    'template:check-updates': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'template:dry-run': { scope: 'workspace-or-dashboard-project', cloud: 'fail-closed' },
    'template:preflight': { scope: 'workspace', cloud: 'fail-closed' },
    'template:pull-latest': { scope: 'workspace', cloud: 'fail-closed' },
    'template:apply': { scope: 'workspace', cloud: 'fail-closed' },
    'template:validate': { scope: 'workspace', cloud: 'fail-closed' },
    'vwr:sso-status': { scope: 'global', cloud: 'not-applicable' },
    'vwr:sso-login': { scope: 'global', cloud: 'not-applicable' },
    'vwr:setup-device': { scope: 'global', cloud: 'not-applicable' },
    'protohub:open': { scope: 'workspace-or-cloud-shell', cloud: 'handler-routed' },
    'protohub:set-bounds': { scope: 'workspace-or-cloud-shell', cloud: 'handler-routed' },
    'protohub:reload': { scope: 'workspace-or-cloud-shell', cloud: 'handler-routed' },
    'protohub:close': { scope: 'workspace-or-cloud-shell', cloud: 'handler-routed' },
    'phase-progress:subscribe': { scope: 'workspace', cloud: 'handler-routed' },
    'phase-progress:unsubscribe': { scope: 'workspace', cloud: 'handler-routed' },
    'phase-progress:open-review': { scope: 'workspace', cloud: 'fail-closed' },
    'phase-progress:decide-blocked': { scope: 'workspace', cloud: 'fail-closed' },
    'activity-prefs:load': { scope: 'workspace', cloud: 'fail-closed' },
    'activity-prefs:save': { scope: 'workspace', cloud: 'fail-closed' },
    'craft:list-scenes': { scope: 'workspace', cloud: 'fail-closed' },
    'design:openProject': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readDesignSystem': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeProjectMeta': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readSection': { scope: 'workspace', cloud: 'fail-closed' },
    'design:getBuildSnapshotSection': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeSection': { scope: 'workspace', cloud: 'fail-closed' },
    'design:addSection': { scope: 'workspace', cloud: 'fail-closed' },
    'design:deleteSection': { scope: 'workspace', cloud: 'fail-closed' },
    'design:listScreens': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readScreen': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeScreen': { scope: 'workspace', cloud: 'fail-closed' },
    'design:deleteScreen': { scope: 'workspace', cloud: 'fail-closed' },
    // Levels / 3Cs / progression / previs — workspace design-file ops (same posture as
    // the section/screen read-write family above).
    'design:listLevels': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readLevel': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeLevel': { scope: 'workspace', cloud: 'fail-closed' },
    'design:deleteLevel': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readThreeCs': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeThreeCs': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readProgression': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeProgression': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readPrevisSkin': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writePrevisSkin': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readPrevisImages': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readBuildRender': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readGraph': { scope: 'workspace', cloud: 'fail-closed' },
    'design:writeGraph': { scope: 'workspace', cloud: 'fail-closed' },
    'design:listReferences': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readReference': { scope: 'workspace', cloud: 'fail-closed' },
    'design:listLenses': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readLens': { scope: 'workspace', cloud: 'fail-closed' },
    'design:listReferenceDocs': { scope: 'workspace', cloud: 'fail-closed' },
    'design:readReferenceDoc': { scope: 'workspace', cloud: 'fail-closed' },
    'design:aiChat': { scope: 'workspace', cloud: 'fail-closed' },
    'design:aiStreamStart': { scope: 'workspace', cloud: 'fail-closed' },
    'design:aiStreamCancel': { scope: 'workspace', cloud: 'fail-closed' },
    'design:exportFoundryPackage': { scope: 'workspace', cloud: 'fail-closed' },
    'design:getBuildUpdateStatus': { scope: 'workspace', cloud: 'fail-closed' },
    'design:commitBuildSnapshot': { scope: 'workspace', cloud: 'fail-closed' },
    'design:cancelExport': { scope: 'workspace', cloud: 'fail-closed' },
    'design:getReconcileStatus': { scope: 'workspace', cloud: 'fail-closed' },
    'design:reconcileFromChat': { scope: 'workspace', cloud: 'fail-closed' },
    'design:applyReconcile': { scope: 'workspace', cloud: 'fail-closed' },
    'design:captureLevelsFromBuild': { scope: 'workspace', cloud: 'fail-closed' },
    'design:applyLevelCapture': { scope: 'workspace', cloud: 'fail-closed' },
    'design:judgeLevel': { scope: 'workspace', cloud: 'fail-closed' },
    'design:reviseLevel': { scope: 'workspace', cloud: 'fail-closed' },
    'tv:input:relay': { scope: 'global', cloud: 'not-applicable' },
};
export const PUSH_PROJECT_METADATA = {
    'cloud-projects:invalidated': { scope: 'global' },
    'agent:stream': { scope: 'workspace' },
    'preview:cloud-update': { scope: 'workspace' },
    'game-state:update': { scope: 'workspace' },
    'agent:ask-user-question': { scope: 'workspace' },
    'deps:request': { scope: 'workspace' },
    // crucible#3491 — same workspace-scoped push posture as `deps:request`.
    'template-file-override:request': { scope: 'workspace' },
    'interview:plan-updated': { scope: 'workspace' },
    'dev:output': { scope: 'workspace' },
    'game:create:progress': { scope: 'global' },
    'create:build-step': { scope: 'global' },
    'create:setup-ready': { scope: 'global' },
    'asset-review:ready': { scope: 'workspace' },
    'asset-review:unreachable': { scope: 'workspace' },
    'asset-review:closed': { scope: 'workspace' },
    'build-asset-review:gate-released': { scope: 'workspace' },
    'intake:ready': { scope: 'workspace' },
    'intake:visual': { scope: 'workspace' },
    'preview:construction-event': { scope: 'workspace' },
    'publish:progress': { scope: 'workspace' },
    'deploy:status': { scope: 'workspace' },
    'editor:error': { scope: 'global' },
    'app:update-downloaded': { scope: 'global' },
    'app:update-error': { scope: 'global' },
    'app:helper-integrity-fail': { scope: 'global' },
    'template:progress': { scope: 'workspace' },
    'vwr:command-output': { scope: 'global' },
    'protohub:load-state': { scope: 'workspace' },
    'phase-progress:update': { scope: 'workspace' },
    'preview:request-close': { scope: 'workspace' },
    'preview:pairing-lost': { scope: 'workspace' },
    'interview:auto-seeded': { scope: 'workspace' },
    // crucible#2715 — 'global' (not 'workspace') because the payload carries
    // no gameId/project-authority fields yet; `shouldDeliverPush`'s workspace
    // check would drop every delivery under project-authority routing since
    // it could never match. No renderer subscriber exists yet either — this
    // is the IPC-layer half of surfacing the PR URL (see the channel's own
    // docblock in ipc-channels.ts). Revisit as 'workspace' if/when a
    // gameId-aware renderer subscriber is added.
    'art:deploy-fallback-pr': { scope: 'global' },
    'activity:state': { scope: 'workspace' },
    'activity:checkpoint': { scope: 'workspace' },
    'activity:template-warning': { scope: 'workspace' },
    'vm:lifecycle': { scope: 'global' },
    'vm:connection-state': { scope: 'global' },
    'vm:message-stream': { scope: 'global' },
    'design:aiStream:chunk': { scope: 'workspace' },
    'design:export:progress': { scope: 'workspace' },
    'build:autoPipeline:progress': { scope: 'workspace' },
};
//# sourceMappingURL=project-channel-metadata.js.map